<script>
    import { onDestroy, onMount } from "svelte";
    import { PRConnect } from "@projectroom/ui-lib";
    import { planStore } from "./store/plan-store";
    import { messaging } from "./store/messaging-store";
    import GameBuilderSidebar from "./game-builder/shell/GameBuilderSidebar.svelte";
    import GameBuilderTopTabs from "./game-builder/shell/GameBuilderTopTabs.svelte";
    import AssetSlotsContext from "./game-builder/AssetSlotsContext.svelte";
    import CharacterContext from "./game-builder/CharacterContext.svelte";
    import MapContext from "./game-builder/MapContext.svelte";
    import PatternContext from "./game-builder/PatternContext.svelte";
    import SkillContext from "./game-builder/SkillContext.svelte";
    import TuningContext from "./game-builder/TuningContext.svelte";
    import MainScreenContext from "./game-builder/MainScreenContext.svelte";
    import DesignContext from "./game-builder/DesignContext.svelte";
    import SoundContext from "./game-builder/SoundContext.svelte";
    import SharePreviewContext from "./game-builder/SharePreviewContext.svelte";

    export let currentRoute;

    let botId;
    let isLoaded = false;
    let saveStatus = "idle"; // idle | saving | saved
    let previewKey = 0;
    // 프리뷰 셸이 가리키는 **게임 번들 버전**. ensureGameShell 응답에서 받아 iframe URL 에 싣는다
    // (아래 previewSrc 주석). null = 아직 못 받음 → iframe 을 띄우지 않는다.
    let shellVersion = null;
    let errorMessage = "";
    let isNarrow = false; // ≤1024px → 프리뷰를 드로어로
    let previewOpen = true;

    // 저작 상태. 서버 BOT_GAME_SETTINGS 한 행과 1:1이다.
    let bot = { id: "", title: "", pageType: "GAME", charId: "" };
    // 게시 기준 두 날짜(BOTS 테이블). 저장할 때마다 서버가 응답 header 에 최신값을 실어보낸다
    // (Bot.js touchBotDate) → 2차 라운드트립 없이 '미반영 변경' 점을 갱신할 수 있다.
    let lastUpdateDate = null;
    let lastPostDate = null;
    // 한 번도 게시 안 했으면 false 다 — 그때는 '미반영'이 아니라 '아직 게시 전'이라
    // 점을 띄우는 대신 첫 클릭이 바로 게시로 간다(BuilderLayout isLatestUpdated 과 같은 규칙).
    // [맵] 탭이 고를 수 있는 스킬 = '떨어지게 하기'를 켠 것만.
    // ⚠️ 캐릭터 스킬 전용(드롭 안 켬)까지 보여주면, 맵에서 골라 놓고도 안 떨어져 혼란스럽다.
    $: dropSkills = (settings.skills.items || []).filter((s) =>
        (settings.skills.drops || []).includes(s.id),
    );
    $: hasUnpublished = !!lastPostDate && new Date(lastPostDate) < new Date(lastUpdateDate);
    let settings = {
        gameType: "VERTICAL_SHOOTER",
        assets: {},
        // { items: [저작 스킬], drops: [떨어뜨릴 스킬 id] }. 비면 런타임이 내장 세트로 논다.
        skills: { items: [], drops: [] },
        characters: [],
        maps: [],
        patterns: [],
        params: {},
        mainTitle: "",
        mainSubtitle: "",
        startLabel: "",
        // 메인 화면 구성(상단바·스탯바·맵 캐러셀 접두어·하단 버튼). 서버 MAIN 컬럼 한 덩어리.
        main: {},
        // 성장 곡선(레벨·경험치). 화면 구성이 아니라 규칙이라 컬럼이 따로다.
        progression: {},
        // 소리 { slots:{슬롯:assetId}, bgmVolume, sfxVolume, vibrate }. 서버 SOUNDS 컬럼 한 덩어리.
        // ⚠️ 그림(assets)과 **다른 맵**이다 — 런타임이 받는 방법이 달라서다(그림=텍스처, 음원=디코드).
        sound: {},
    };
    // 슬롯 썸네일용 **슬롯키→URL**. 서버가 설정과 함께 내려준 resolve 결과를 담아둔다.
    let assetUrls = {};
    // 캐릭터 썸네일용 **assetId→URL**. 캐릭터 그림은 슬롯이 아니라 캐릭터 객체 안에 있어
    // 키 체계가 다르다(같은 맵에 섞으면 슬롯 이름과 assetId 가 충돌할 수 있다).
    let charAssetUrls = {};

    // 순서 = **저작 순서**. 앞의 탭은 뒤의 탭을 몰라도 되고, 뒤로 갈수록 앞에서 만든 것을 재료로 쓴다.
    //   메인 화면   게임의 얼굴. 의존 없음 — 저작자가 제일 먼저 보고 싶어 하는 화면이라 첫 탭이다.
    //   캐릭터·기본 이미지   그림을 만드는 탭. 의존 없음.
    //   적 편대     [기본 이미지]의 적 그림을 격자에 배치한다 → 적 그림 뒤에 와야 한다.
    //   맵          ⚠️ **조립 탭**이다(난이도 배율 + 쓸 편대 고르기 + 에셋 부분 덮어쓰기).
    //               편대도 에셋도 없는 상태에서 열면 고를 게 비어 있다 → 맨 뒤.
    const tabList = [
        { value: "main", label: "메인 화면" },
        { value: "character", label: "캐릭터" },
        { value: "asset", label: "기본 이미지" },
        { value: "pattern", label: "적 편대" },
        { value: "skill", label: "스킬" },
        { value: "tuning", label: "난이도" },
        { value: "map", label: "맵" },
        { value: "design", label: "디자인" },
        { value: "sound", label: "소리" },
        // 마지막 단계 = 내보내기. 저장소도 혼자 다르다(BOTS 한 행 — SharePreviewContext 주석).
        { value: "share", label: "공유" },
    ];
    let currentTab = "main";

    $: currentTabLabel = tabList.find((t) => t.value === currentTab)?.label || "";

    onMount(() => {
        botId = currentRoute.namedParams.botId;
        loadAll();
        syncPreviewShell();

        // 반응형: 좁은 화면에선 프리뷰를 사이드가 아니라 오버레이 드로어로(ChatBuilder와 같은 경계).
        const mql = window.matchMedia("(max-width: 1023px)");
        const apply = () => {
            isNarrow = mql.matches;
            previewOpen = !mql.matches;
        };
        apply();
        mql.addEventListener("change", apply);
        window.addEventListener("message", onPreviewMessage);
        return () => {
            mql.removeEventListener("change", apply);
            window.removeEventListener("message", onPreviewMessage);
        };
    });

    // 게시 완료 통지(GameBuilderLayout 이 게시 성공 시 보냄): 방금 전부 게시됐으니
    // 게시일자를 최신 수정일자로 맞춰 sticky '게시하기'의 미반영 점을 즉시 끈다
    // (다음 저장이나 새로고침을 기다리지 않는다).
    // ⚠️ 구독 즉시 오는 **현재 값 재생**은 버린다(GameBuilderLayout 쪽 주석과 같은 이유).
    let publishMsgReplayed = false;
    const publishMsgSubsc = messaging.subscribe((message) => {
        if (!publishMsgReplayed) {
            publishMsgReplayed = true;
            return;
        }
        if (message?.target === "GameBuilder" && message.action === "published") {
            // 한 번도 저장 안 한 게임은 LAST_UPDATE_DATE 가 비어 있다 → 그때도 '게시된 적 있음'이
            // 되도록 지금 시각을 넣는다(다음 클릭이 팝업으로 가야 한다. 서버 LAST_POST_DATE 는
            // 이미 찍혔고, 다음 로드에서 진짜 값으로 덮인다).
            lastPostDate = lastUpdateDate || new Date().toISOString();
        }
    });
    onDestroy(() => publishMsgSubsc());

    /**
     * 프리뷰 iframe 이 "설정 주세요" 하면 **빌더가 대신 받아** 넘긴다.
     *
     * ⚠️ iframe 이 직접 /api/Bot/getGameConfig 를 부를 수 없다 — 그 API 는
     *    `Authorization: Bearer` **헤더**로 인증하는데(쿠키가 아니다) iframe 의 fetch 에는
     *    그 헤더가 없어서 NOT_AUTHORIZED 가 난다. 토큰을 iframe 에 넘기면 새는 길이 생기고,
     *    공개 엔드포인트로 빼면 미공개 저작 설정이 botId 만 알면 보인다.
     * ⚠️ 저장 직후에도 최신이어야 하므로 **그때그때 다시 받아서** 보낸다(캐시하지 않는다).
     */
    const onPreviewMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== "game-preview-ready") return;
        const target = event.source;
        PRConnect.send("/api/Bot/getGameConfig", { botId }, (data) => {
            const config = data.header.status === "success" ? data.result : null;
            target?.postMessage(
                { type: "game-preview-config", config },
                window.location.origin,
            );
        });
    };

    /**
     * 프리뷰 셸이 지금 게임 번들 버전으로 구워져 있는지 서버에 한 번 확인시킨다.
     *
     * 셸 HTML 에는 번들의 **버전 경로가 문자열로 구워져** 있어서, 런타임을 새로 배포해도
     * 이미 구워진 셸은 옛 번들을 계속 문다. 예전엔 되살릴 방법이 게시뿐이었다 —
     * 프리뷰 보려고 공개본을 내보내는 꼴이라, 빌더에 들어올 때 서버가 알아서 맞추게 했다.
     *
     * ⚠️ 서버는 **버전이 다를 때만** 다시 굽는다(HEAD 한 번으로 비교). 매 진입마다 S3 에
     *    올리지 않는다. 그래서 여기서도 조건 없이 부르면 된다.
     * ⚠️ loadAll 과 **나란히** 보낸다. 앞에 세우면 이 왕복이 끝날 때까지 빌더가 비어 있는다.
     * ⚠️ 실패는 삼킨다 — 셸이 좀 낡았을 뿐 저작은 그대로 되어야 한다.
     */
    const syncPreviewShell = () => {
        PRConnect.send("/api/Bot/ensureGameShell", { botId }, (data) => {
            if (data.header.status === "success") {
                // 셸 URL 의 캐시 키가 된다(previewSrc). 번들을 새로 배포하면 이 값이 달라지고,
                // 달라진 URL 은 브라우저가 무조건 새로 받는다.
                shellVersion = data.result?.version || "na";
                // 같은 버전으로 다시 구운 경우(메타데이터가 없던 옛 셸)는 URL 이 안 바뀌므로
                // 여기서 한 번 밀어 준다.
                if (data.result?.rebuilt) previewKey += 1;
            } else {
                // 이 확인에 실패해도 프리뷰는 떠야 한다 — 셸이 좀 낡았을 뿐이다.
                shellVersion = "na";
            }
        });
    };

    const loadAll = () => {
        // ⚠️ getBotInfo 의 파라미터는 `id` 다(`botId` 가 아니다). 게임 봇에는 시나리오·버튼이 없어
        //    응답 대부분이 비지만, 프리뷰 경로에 필요한 charId 가 여기 들어 있다.
        PRConnect.send("/api/Bot/getBotInfo", { id: botId }, (data) => {
            if (data.header.status === "success") {
                bot = {
                    ...bot,
                    ...(data.result?.botData || {}),
                    id: botId,
                    charId: data.result?.botChar?.charId || "",
                };
                lastUpdateDate = bot.lastUpdateDate || null;
                lastPostDate = bot.lastPostDate || null;
            }
            loadSettings();
        });
    };

    const loadSettings = () => {
        PRConnect.send("/api/Bot/getGameConfig", { botId }, (data) => {
            if (data.header.status !== "success") {
                // 설정 행이 없으면 저장이 조용히 0건이 된다(저작자에겐 '저장했는데 안 바뀐다'로만 보인다)
                // → 화면에서 바로 알려준다. 보통 마이그레이션 미적용이 원인이다.
                errorMessage = "게임 설정을 불러오지 못했습니다. 저장이 반영되지 않을 수 있어요.";
                isLoaded = true;
                return;
            }
            const c = data.result || {};
            settings = {
                gameType: c.gameType || "VERTICAL_SHOOTER",
                // ⚠️ getGameConfig 의 assets 는 **URL** 맵이다(런타임이 쓰는 모양).
                //    저작은 assetId 를 들고 있어야 하므로 두 맵을 따로 둔다 — 여기에 URL 을
                //    그대로 넣고 저장하면 ASSETS 컬럼이 ref 가 아니라 URL 로 오염된다.
                assets: { ...(c.assetIds || {}) },
                // ⚠️ c.skills(런타임용, URL 박힘)가 아니라 skillAssetIds(저작용 ref)를 쓴다 —
                //    캐릭터·맵과 같은 이유. URL 을 되돌려 저장하면 assetId 가 URL 로 오염된다.
                skills: {
                    items: Array.isArray(c.skillAssetIds) ? c.skillAssetIds : [],
                    drops: Array.isArray(c.skills?.drops) ? c.skills.drops : [],
                },
                // ⚠️ characters(URL 이 박힌 런타임용)가 아니라 characterAssetIds(저작용 ref)를 쓴다.
                //    URL 을 되돌려 저장하면 CHARACTERS 의 assetId 가 URL 로 오염된다.
                characters: Array.isArray(c.characterAssetIds) ? c.characterAssetIds : [],
                // 맵도 같은 이유로 저작용 ref 쪽(mapAssetIds)을 쓴다.
                maps: Array.isArray(c.mapAssetIds) ? c.mapAssetIds : [],
                patterns: Array.isArray(c.patterns) ? c.patterns : [],
                params: c.params || {},
                mainTitle: c.main?.title || "",
                mainSubtitle: c.main?.subtitle || "",
                startLabel: c.main?.startLabel || "",
                // ⚠️ c.main 이 아니라 mainAssetIds 를 쓴다 — c.main 의 버튼에는 아이콘이 **URL** 로
                //    박혀 있다(런타임이 쓰는 모양). 그대로 저장하면 ref 가 URL 로 오염돼
                //    자산을 교체해도 안 바뀐다. 캐릭터·맵과 같은 이유.
                main: c.mainAssetIds || {},
                progression: c.progression || {},
                // ⚠️ c.sound(런타임용, URL 박힘)가 아니라 soundAssetIds(저작용 ref)를 쓴다 —
                //    그림·캐릭터와 같은 이유. URL 을 되돌려 저장하면 assetId 가 URL 로 오염돼
                //    음원을 교체해도 안 바뀐다.
                sound: c.soundAssetIds || {},
            };
            assetUrls = { ...(c.assets || {}) };
            // 서버는 캐릭터를 두 벌로 준다: 런타임용(URL 박힘)과 저작용(ref). 같은 순서라
            // 짝지으면 assetId→URL 을 만들 수 있다 — 썸네일에 필요한 건 이 맵이다.
            charAssetUrls = {};
            (c.characterAssetIds || []).forEach((ref, i) => {
                const runtime = (c.characters || [])[i];
                if (ref?.playerAssetId && runtime?.playerUrl) charAssetUrls[ref.playerAssetId] = runtime.playerUrl;
                if (ref?.bulletAssetId && runtime?.bulletUrl) charAssetUrls[ref.bulletAssetId] = runtime.bulletUrl;
                if (ref?.ingameAssetId && runtime?.ingameUrl) charAssetUrls[ref.ingameAssetId] = runtime.ingameUrl;
            });
            // 맵 오버라이드도 같은 방식으로 짝지어 assetId→URL 을 만든다(슬롯 이름은 같고 값만 다르다).
            (c.mapAssetIds || []).forEach((ref, i) => {
                const runtime = (c.maps || [])[i];
                for (const [slot, id] of Object.entries(ref?.assets || {})) {
                    const url = runtime?.assets?.[slot];
                    if (id && url) charAssetUrls[id] = url;
                }
            });
            // 스테이지 보스 그림도 짝지어 둔다.
            // ⚠️ 맵은 순번으로, 그 안의 스테이지는 **id** 로 짝짓는다 — 런타임 스테이지 목록은
            //    형식이 깨진 것이 빠질 수 있어 저작 목록과 길이가 다를 수 있다.
            (c.mapAssetIds || []).forEach((ref, i) => {
                const runStages = new Map(((c.maps || [])[i]?.stages || []).map((st) => [st.id, st]));
                for (const st of ref?.stages || []) {
                    const run = runStages.get(st?.id)?.boss;
                    if (!st?.boss || !run) continue;
                    if (st.boss.assetId && run.url) charAssetUrls[st.boss.assetId] = run.url;
                    if (st.boss.hitAssetId && run.hitUrl) charAssetUrls[st.boss.hitAssetId] = run.hitUrl;
                    if (st.boss.deathAssetId && run.deathUrl) charAssetUrls[st.boss.deathAssetId] = run.deathUrl;
                }
            });
            // 스킬 그림도 같은 방식으로 짝지어 assetId→URL 을 만든다.
            // ⚠️ 순번이 아니라 **스킬 id** 로 짝짓는다 — 서버가 형식이 깨진 스킬(모르는 효과,
            //    중복 id)을 걸러내므로 런타임 items 와 저작 items 의 길이·순서가 다를 수 있다.
            const runtimeSkills = new Map((c.skills?.items || []).map((it) => [it.id, it]));
            for (const ref of c.skillAssetIds || []) {
                const run = runtimeSkills.get(ref?.id);
                if (ref?.iconAssetId && run?.iconUrl) charAssetUrls[ref.iconAssetId] = run.iconUrl;
                if (ref?.dropAssetId && run?.dropUrl) charAssetUrls[ref.dropAssetId] = run.dropUrl;
                if (ref?.effectAssetId && run?.effectUrl) charAssetUrls[ref.effectAssetId] = run.effectUrl;
            }
            // 소리도 짝지어 미리듣기용 URL 맵을 만든다.
            // ⚠️ 여기는 **슬롯 키로** 짝짓는다 — sound.urls 와 soundAssetIds.slots 가 같은 키를
            //    쓰므로 순번을 맞출 일이 없다(버튼·스킬이 id 로 짝짓는 것과 같은 이유의 반대쪽).
            for (const [slot, id] of Object.entries(c.soundAssetIds?.slots || {})) {
                const url = c.sound?.urls?.[slot];
                if (id && url) charAssetUrls[id] = url;
            }
            // 하단 버튼 아이콘도 마찬가지.
            // ⚠️ 여기만 **순번이 아니라 버튼 id** 로 짝짓는다 — 서버가 형식이 깨진 버튼을 걸러내므로
            //    런타임 buttons 와 저작 buttons 의 길이·순서가 다를 수 있다.
            const runtimeButtons = new Map((c.main?.buttons || []).map((b) => [b.id, b]));
            for (const ref of c.mainAssetIds?.buttons || []) {
                const url = runtimeButtons.get(ref?.id)?.iconUrl;
                if (ref?.iconAssetId && url) charAssetUrls[ref.iconAssetId] = url;
            }
            isLoaded = true;
        });
    };

    /**
     * 탭이 만진 것만 보낸다(서버 modifyGameSettings 가 부분 저장).
     * 전체를 매번 보내면 한 탭의 낡은 값이 다른 탭의 최신 저장을 덮어쓴다.
     */
    const save = (patch) => {
        saveStatus = "saving";
        errorMessage = "";
        PRConnect.send("/api/Bot/modifyGameSettings", { botId, ...patch }, (data) => {
            if (data.header.status === "success") {
                saveStatus = "saved";
                // 서버가 응답 header 에 실어보낸 최신 날짜로 '미반영 변경' 점을 갱신한다.
                if (data.header.lastUpdateDate) {
                    lastUpdateDate = data.header.lastUpdateDate;
                    lastPostDate = data.header.lastPostDate ?? lastPostDate;
                }
                // 프리뷰는 저장된 초안을 API로 다시 읽는다 → iframe만 새로 띄우면 최신이 된다.
                previewKey += 1;
            } else {
                saveStatus = "idle";
                errorMessage = "저장하지 못했습니다. 잠시 후 다시 시도해주세요.";
            }
        });
    };

    const onAssetsChange = (e) => {
        settings.assets = e.detail.assets;
        assetUrls = e.detail.urls;
        save({ assets: settings.assets });
    };
    const onMapsChange = (e) => {
        settings.maps = e.detail;
        save({ maps: settings.maps });
    };
    const onCharactersChange = (e) => {
        settings.characters = e.detail;
        save({ characters: settings.characters });
    };
    // 캐릭터 탭에서 새로 고른 그림의 URL 을 썸네일 맵에 넣어둔다(저장값은 assetId 쪽에만 들어간다).
    const onCharAssetUrl = (e) => {
        charAssetUrls = { ...charAssetUrls, [e.detail.assetId]: e.detail.url };
    };
    const onPatternsChange = (e) => {
        settings.patterns = e.detail;
        save({ patterns: settings.patterns });
    };
    const onSkillsChange = (e) => {
        settings.skills = e.detail;
        save({ skills: settings.skills });
    };
    const onParamsChange = (e) => {
        settings.params = e.detail;
        save({ params: settings.params });
    };
    // 문구 3개와 구성(main)은 한 번에 보낸다 — 같은 UPDATE 로 나가므로 나눌 이유가 없다.
    const onMainChange = (e) => {
        settings.mainTitle = e.detail.mainTitle;
        settings.mainSubtitle = e.detail.mainSubtitle;
        settings.startLabel = e.detail.startLabel;
        settings.main = e.detail.main;
        save({
            mainTitle: settings.mainTitle,
            mainSubtitle: settings.mainSubtitle,
            startLabel: settings.startLabel,
            main: settings.main,
        });
    };
    /**
     * UI 색. `main` 안(`theme`)에 들어가므로 **기존 main 을 유지한 채** 그 칸만 갈아끼운다
     * (통째로 덮으면 [메인 화면] 탭이 저작한 버튼·상단 설정이 날아간다).
     */
    const onThemeChange = (e) => {
        settings.main = { ...(settings.main || {}), theme: e.detail };
        save({ main: settings.main });
    };
    /** 글꼴도 `main` 안에 들어간다 — 테마와 같은 이유로 기존 main 을 유지한 채 그 칸만 바꾼다. */
    const onFontChange = (e) => {
        settings.main = { ...(settings.main || {}), font: e.detail };
        save({ main: settings.main });
    };
    const onProgressionChange = (e) => {
        settings.progression = e.detail;
        save({ progression: settings.progression });
    };
    /**
     * 소리. `main` 과 달리 **자기 컬럼(SOUNDS)** 이라 통째로 보낸다.
     * ⚠️ 파라미터 이름이 `sound` 다(컬럼은 SOUNDS) — 서버 매퍼가 그 이름으로 받는다.
     */
    const onSoundChange = (e) => {
        settings.sound = e.detail;
        save({ sound: settings.sound });
    };

    /**
     * 링크 프리뷰(공유 카드)만 **다른 저장소**다 — BOT_GAME_SETTINGS 가 아니라 BOTS 한 행이라
     * modifyGameSettings 가 아니라 modifyBotStatisticsData 로 나간다.
     *
     * ⚠️⚠️ 매퍼 `updateBotStatisticsData` 는 `<if>` 없이 **전 컬럼을 통째로 SET** 한다.
     *    한 칸만 보내면 제목·부제·GA 코드·커스텀 URL 이 전부 NULL 로 덮인다(봇 빌더 [설정]
     *    탭이 매번 전체를 보내는 이유다). 그래서 여기도 **BOTS 한 행을 통째로** 보낸다 —
     *    값은 getBotInfo 로 받아 둔 `bot` 에서 가져오고, 없는 칸은 undefined 가 아니라
     *    **명시적 null** 로 넣는다(undefined 를 흘리면 바인딩 단계에서 터진다).
     * ⚠️ 서버가 이 핸들러에서는 touchBotDate 를 안 부른다(봇 빌더 [설정] 탭이 키 입력마다
     *    저장을 쏘는 구조라 날짜가 요동치기 때문). 게시 대기 점은 떠 있어야 하므로 —
     *    링크 프리뷰는 **게시해야** 공개 주소에 반영된다 — 여기서 날짜만 따로 올린다.
     */
    const saveBotProfile = () => {
        saveStatus = "saving";
        errorMessage = "";
        PRConnect.send(
            "/api/Bot/modifyBotStatisticsData",
            {
                id: botId,
                title: bot.title ?? null,
                subTitle: bot.subTitle ?? null,
                scriptCode: bot.scriptCode ?? null,
                designateUrl: bot.designateUrl ?? null,
                thumbnailImageContent: bot.thumbnailImageContent ?? null,
                thumbnailImageTitle: bot.thumbnailImageTitle ?? null,
                thumbnailImageSubTitle: bot.thumbnailImageSubTitle ?? null,
            },
            (data) => {
                if (data.header.status !== "success") {
                    saveStatus = "idle";
                    errorMessage = "저장하지 못했습니다. 잠시 후 다시 시도해주세요.";
                    return;
                }
                saveStatus = "saved";
                // 게시 대기 점 갱신용. 실패해도 저장은 끝났으니 조용히 넘어간다.
                PRConnect.send("/api/Bot/modifyBotUpdateDate", { id: botId }, (res) => {
                    if (res.header.status !== "success") return;
                    lastUpdateDate = res.result?.lastUpdateDate ?? lastUpdateDate;
                    lastPostDate = res.result?.lastPostDate ?? lastPostDate;
                });
            },
            () => {
                saveStatus = "idle";
                errorMessage = "저장하지 못했습니다. 잠시 후 다시 시도해주세요.";
            },
        );
    };

    const onShareChange = (e) => {
        bot = { ...bot, ...e.detail };
        saveBotProfile();
    };

    /**
     * 게시 다이얼로그(주소·QR·변경사항 반영)는 **GameBuilderLayout 이 소유**한다 —
     * 다른 빌더와 같은 구조다(버튼은 에디터 sticky 툴바, 팝업은 레이아웃).
     * 여기서는 열기만 요청하고, 실제 postChangeBotData 는 레이아웃이 보낸다.
     *
     * ⚠️ 예전엔 이 버튼이 곧바로 게시를 쏘고 saveStatus 만 "saved" 로 바꿨다. 그 칩은 원래
     *    거의 항상 "저장됨"이라 화면 변화가 없어서 "게시가 안 된다"로 보였다.
     */
    const publish = () => {
        messaging.set({
            target: "GameBuilderLayout",
            action: "openPublish",
            isPosted: !!lastPostDate,
        });
    };

    /**
     * 프리뷰 iframe. 게임 셸(index.html)은 설정을 굽지 않고 API로 초안을 읽으므로,
     * 저장 후 키만 바꿔 다시 띄우면 최신 상태가 나온다.
     *
     * ⚠️⚠️ 캐시 키가 **번들 버전 + 세션 카운터** 두 조각인 이유:
     *   셸 안에는 번들 경로가 문자열로 구워져 있고 번들은 immutable 1년 캐시다. 그래서
     *   **셸이 브라우저에 고이면 번들을 새로 배포해도 그 브라우저는 옛 번들에 갇힌다.**
     *   - `previewKey` 는 `let previewKey = 0` 이라 **페이지를 새로 열면 0 으로 돌아간다** →
     *     세션 안(저장·새로고침 버튼)에서는 캐시를 깨지만 **세션 사이에는 못 깬다.**
     *     실제로 이 구멍으로 "배포했는데 프리뷰에 반영이 안 된다"를 겪었다.
     *   - `shellVersion` 이 그 자리를 메운다. 배포로 버전이 오르면 URL 이 달라져 새로 받는다
     *     (번들에 버전 경로를 두는 것과 같은 원리 — 캐시를 무력화하는 대신 **주소를 바꾼다**).
     *
     * ⚠️ charId 가 없으면 경로가 어긋난다(//index.html) → 아무것도 안 띄운다.
     * ⚠️ shellVersion 을 기다리는 이유: 먼저 띄우면 버전이 도착할 때 URL 이 바뀌어 **두 번 로드된다**.
     *    두 요청은 나란히 나가므로(onMount) 기다리는 시간이 늘지 않는다.
     */
    $: previewSrc =
        botId && bot.charId && shellVersion
            ? `/static/avatalk/${botId}/${bot.charId}/index.html?v=${shellVersion}-${previewKey}`
            : "";
</script>

{#if isLoaded}
    <div class="builder-shell" class:creepong={planStore.isCreepong()}>
        <div
            class="tw-mx-auto tw-flex tw-h-full tw-w-full tw-overflow-hidden tw-bg-background tw-font-sans tw-text-foreground"
            style="max-width: var(--app-max-w, 1920px)"
        >
            <GameBuilderSidebar {tabList} {currentTab} on:change={(e) => (currentTab = e.detail)} />

            <div class="tw-flex tw-min-w-0 tw-flex-1 tw-justify-center tw-overflow-hidden tw-bg-background">
                <div class="tw-flex tw-w-full tw-min-w-0">
                    <!-- 에디터: 메인 작업영역 -->
                    <main class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
                        <!-- 폰(<768px): 좌측 사이드바 대신 상단 가로 탭 -->
                        <GameBuilderTopTabs {tabList} {currentTab} on:change={(e) => (currentTab = e.detail)} />

                        <div class="tw-flex-1 tw-overflow-y-auto">
                            <!-- 스티키 액션 툴바: 스크롤/모바일에서도 에디터 상단에 고정 → 게시·저장 상태가 항상 보인다. -->
                            <div class="tw-sticky tw-top-0 tw-z-20 tw-border-b tw-border-border tw-bg-background/90 tw-backdrop-blur">
                                <div class="tw-mx-auto tw-flex tw-w-full tw-max-w-[860px] tw-items-center tw-justify-between tw-gap-3 tw-px-6 tw-py-2.5">
                                    <div class="tw-min-w-0">
                                        <div class="tw-hidden tw-truncate tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-muted-foreground sm:tw-block">
                                            {bot.title || "게임"} · 세로 슈팅
                                        </div>
                                        <h1 class="tw-truncate tw-text-base tw-font-semibold tw-text-foreground sm:tw-text-lg">
                                            {currentTabLabel}
                                        </h1>
                                    </div>
                                    <div class="tw-relative tw-flex tw-shrink-0 tw-items-center tw-gap-2 sm:tw-gap-3">
                                        <!-- 자동 저장 상태(pill). 저장은 필드 변경마다 자동 → 버튼 대신 상태만. -->
                                        {#if errorMessage}
                                            <div class="tw-inline-flex tw-select-none tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-destructive/30 tw-bg-destructive/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-destructive" aria-live="polite">
                                                {errorMessage}
                                            </div>
                                        {:else}
                                            <div
                                                class="save-indicator tw-inline-flex tw-select-none tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-transition-colors {saveStatus ===
                                                'saving'
                                                    ? 'tw-border-border tw-bg-muted tw-text-muted-foreground'
                                                    : saveStatus === 'saved'
                                                      ? 'tw-border-emerald-500/30 tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-400'
                                                      : 'tw-border-transparent tw-bg-muted/50 tw-text-muted-foreground/70'}"
                                                aria-live="polite"
                                            >
                                                {#if saveStatus === "saving"}
                                                    <svg class="save-spinner tw-h-3.5 tw-w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                                                    <span>저장 중…</span>
                                                {:else}
                                                    <svg class="tw-h-3.5 tw-w-3.5 {saveStatus === 'saved' ? '' : 'tw-opacity-70'}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                    <span>저장됨</span>
                                                {/if}
                                            </div>
                                        {/if}
                                        <button
                                            type="button"
                                            class="tw-inline-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-bg-primary tw-px-4 tw-text-sm tw-font-medium tw-text-primary-foreground tw-transition-colors hover:tw-bg-primary/90"
                                            on:click={publish}
                                        >
                                            {#if hasUnpublished}
                                                <span class="publish-dot tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-foreground" title="게시되지 않은 변경사항이 있어요"></span>
                                            {/if}
                                            게시하기
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="tw-mx-auto tw-w-full tw-max-w-[860px] tw-px-6 tw-pb-6 tw-pt-5">
                                <div class="tw-space-y-4">
                                    {#if currentTab === "main"}
                                        <MainScreenContext
                                            {bot}
                                            mainTitle={settings.mainTitle}
                                            mainSubtitle={settings.mainSubtitle}
                                            startLabel={settings.startLabel}
                                            main={settings.main}
                                            assets={settings.assets}
                                            urls={assetUrls}
                                            iconUrls={charAssetUrls}
                                            botTitle={bot.title}
                                            on:change={onMainChange}
                                            on:assets={onAssetsChange}
                                            on:assetUrl={onCharAssetUrl}
                                        />
                                    {:else if currentTab === "character"}
                                        <CharacterContext
                                            {bot}
                                            characters={settings.characters}
                                            skillItems={settings.skills.items}
                                            urls={charAssetUrls}
                                            on:change={onCharactersChange}
                                            on:assetUrl={onCharAssetUrl}
                                        />
                                    {:else if currentTab === "asset"}
                                        <AssetSlotsContext
                                            {bot}
                                            assets={settings.assets}
                                            urls={assetUrls}
                                            on:change={onAssetsChange}
                                        />
                                    {:else if currentTab === "pattern"}
                                        <PatternContext
                                            patterns={settings.patterns}
                                            urls={assetUrls}
                                            on:change={onPatternsChange}
                                        />
                                    {:else if currentTab === "skill"}
                                        <SkillContext
                                            {bot}
                                            skills={settings.skills}
                                            urls={charAssetUrls}
                                            on:change={onSkillsChange}
                                            on:assetUrl={onCharAssetUrl}
                                        />
                                    {:else if currentTab === "tuning"}
                                        <TuningContext
                                            params={settings.params}
                                            progression={settings.progression}
                                            on:change={onParamsChange}
                                            on:progression={onProgressionChange}
                                        />
                                    {:else if currentTab === "map"}
                                        <MapContext
                                            {bot}
                                            maps={settings.maps}
                                            patterns={settings.patterns}
                                            dropSkills={dropSkills}
                                            urls={charAssetUrls}
                                            on:change={onMapsChange}
                                            on:assetUrl={onCharAssetUrl}
                                        />
                                    {:else if currentTab === "design"}
                                        <DesignContext
                                            theme={settings.main?.theme || {}}
                                            font={settings.main?.font || ""}
                                            on:change={onThemeChange}
                                            on:font={onFontChange}
                                        />
                                    {:else if currentTab === "sound"}
                                        <SoundContext
                                            {bot}
                                            sound={settings.sound}
                                            urls={charAssetUrls}
                                            on:change={onSoundChange}
                                            on:assetUrl={onCharAssetUrl}
                                        />
                                    {:else if currentTab === "share"}
                                        <SharePreviewContext {bot} on:change={onShareChange} />
                                    {/if}
                                </div>
                            </div>
                        </div>
                    </main>

                    <!-- 좁은 화면: 프리뷰 닫혀 있을 때 떠 있는 열기 버튼 -->
                    {#if isNarrow && !previewOpen}
                        <button
                            type="button"
                            class="tw-fixed tw-bottom-5 tw-right-5 tw-z-[998] tw-inline-flex tw-h-11 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-primary tw-px-4 tw-text-sm tw-font-medium tw-text-primary-foreground tw-shadow-lg tw-transition-colors hover:tw-bg-primary/90"
                            on:click={() => (previewOpen = true)}
                            title="미리보기 열기">🔎 미리보기</button
                        >
                    {/if}

                    <!-- 좁은 화면 드로어 배경(탭하면 닫힘) -->
                    {#if isNarrow && previewOpen}
                        <button
                            type="button"
                            class="tw-fixed tw-inset-0 tw-z-[999] tw-cursor-default tw-bg-black/40"
                            on:click={() => (previewOpen = false)}
                            aria-label="미리보기 닫기"
                        ></button>
                    {/if}

                    <!-- 미리보기: 넓은 화면=사이드 / 좁은 화면(≤1024px)=오른쪽 슬라이드 드로어 -->
                    <aside
                        class="tw-flex tw-flex-col tw-bg-background {isNarrow
                            ? 'tw-fixed tw-inset-y-0 tw-right-0 tw-z-[1000] tw-w-[min(440px,100vw)] tw-border-l tw-border-border tw-shadow-2xl tw-transition-transform ' +
                              (previewOpen ? 'tw-translate-x-0' : 'tw-translate-x-full')
                            : 'tw-w-[460px] tw-shrink-0 tw-border-l tw-border-border'}"
                        aria-hidden={isNarrow && !previewOpen}
                    >
                        <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-p-4">
                            <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-border tw-bg-card tw-shadow-sm">
                                <header class="tw-flex tw-min-h-[52px] tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-border tw-px-4">
                                    <h2 class="tw-truncate tw-text-lg tw-font-semibold tw-text-foreground">미리보기</h2>
                                    <div class="tw-flex tw-items-center tw-gap-1">
                                        <button
                                            type="button"
                                            class="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground"
                                            on:click={() => (previewKey += 1)}
                                            aria-label="처음부터 다시"
                                            title="처음부터 다시"
                                        >
                                            <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                                        </button>
                                        {#if isNarrow}
                                            <button
                                                type="button"
                                                class="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground"
                                                on:click={() => (previewOpen = false)}
                                                aria-label="미리보기 닫기"
                                                title="미리보기 닫기"
                                            >
                                                <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                            </button>
                                        {/if}
                                    </div>
                                </header>

                                <!-- 게임은 9:19.5 세로 화면이다. 그 비율로 잡아야 실제 화면과 같게 보인다
                                     (게임도 남는 자리를 검정으로 채우므로 바탕을 검정으로 둔다). -->
                                <div class="tw-flex tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center tw-p-3">
                                    <div class="game-preview-frame">
                                        {#if previewSrc}
                                            {#key previewKey}
                                                <iframe src={previewSrc} title="게임 미리보기" class="tw-block tw-h-full tw-w-full tw-border-0"></iframe>
                                            {/key}
                                        {/if}
                                    </div>
                                </div>

                                <footer class="tw-shrink-0 tw-border-t tw-border-border tw-px-4 tw-py-2.5 tw-text-xs tw-text-muted-foreground">
                                    저장하면 미리보기가 새로 시작됩니다.
                                </footer>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    /* ChatBuilder 와 같은 규격: 상단 고정 탑바(56px) 아래에 앉는다. */
    .builder-shell {
        margin-top: 56px;
        height: calc(100vh - 56px);
        width: 100%;
        overflow: hidden;
        background: hsl(var(--sb-background));
    }

    /* 크리퐁: 상단 네비바 없음 → 전체 높이 사용 */
    .builder-shell.creepong {
        margin-top: 0;
        height: 100vh;
    }

    /* 게임 화면 비율(9:19.5). 높이가 모자라면 높이에 맞추고 폭이 줄어든다. */
    .game-preview-frame {
        aspect-ratio: 9 / 19.5;
        height: 100%;
        max-width: 100%;
        border-radius: 14px;
        overflow: hidden;
        background: #000;
    }

    @keyframes save-spin {
        to {
            transform: rotate(360deg);
        }
    }

    .save-spinner {
        animation: save-spin 0.8s linear infinite;
    }

    /* 게시 대기(미반영 변경) 점: 은은한 맥동으로 시선을 끈다 (봇/챗 빌더와 동일) */
    @keyframes sb-pulse {
        0%,
        100% {
            opacity: 1;
            transform: scale(1);
        }
        50% {
            opacity: 0.45;
            transform: scale(0.85);
        }
    }

    .publish-dot {
        animation: sb-pulse 1.6s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
        .save-spinner,
        .publish-dot {
            animation: none;
        }
    }
</style>
